import { useMemo, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { loadStripe, type Stripe } from '@stripe/stripe-js';
import { Button } from '../ui';
import { formatMoney } from '../../utils/detectCountry';
import { ApiError } from '../../services/api/apiClient';
import type { BecasPaymentInfo } from '../../services/api/becasApiService';

const stripeCache = new Map<string, Promise<Stripe | null>>();

function getStripe(publishableKey: string) {
  let promise = stripeCache.get(publishableKey);
  if (!promise) {
    promise = loadStripe(publishableKey);
    stripeCache.set(publishableKey, promise);
  }
  return promise;
}

const appearance = {
  theme: 'night' as const,
  variables: {
    colorPrimary: '#FFD700',
    colorBackground: '#0a1628',
    colorText: '#f1f5f9',
    colorDanger: '#f43f5e',
    borderRadius: '12px',
    fontFamily: 'Plus Jakarta Sans, system-ui, sans-serif',
  },
};

interface Props {
  payment?: BecasPaymentInfo | null;
  paying: boolean;
  payError: string | null;
  publishableKey?: string;
  onPay: (paymentToken?: string, discountCode?: string) => Promise<void>;
}

export function BecasPaymentForm({ payment, paying, payError, publishableKey, onPay }: Props) {
  const { t } = useTranslation();
  const [discount, setDiscount] = useState('');
  const [accepted, setAccepted] = useState(false);
  const stripePromise = useMemo(
    () => (publishableKey ? getStripe(publishableKey) : null),
    [publishableKey],
  );

  const amount = payment?.amount ?? 0;
  const currency = (payment?.currency || 'USD').toUpperCase();
  const amountLabel = payment?.amount != null
    ? formatMoney(payment.amount, currency, payment.locale)
    : null;
  const canUseStripe = Boolean(stripePromise && amount > 0);

  return (
    <div className="space-y-5">
      <div className="rounded-2xl border border-brand-yellow/30 bg-brand-yellow/5 p-4">
        <p className="text-sm text-dark-400">{payment?.plan_name || t('becas.payment.planFallback')}</p>
        {amountLabel && <p className="text-3xl font-black text-brand-yellow mt-1">{amountLabel}</p>}
      </div>

      <label className="block">
        <span className="block text-sm font-semibold text-white mb-2">{t('becas.payment.discount')}</span>
        <input
          value={discount}
          onChange={(e) => setDiscount(e.target.value.toUpperCase())}
          placeholder={t('becas.payment.discountPlaceholder')}
          className="w-full rounded-xl bg-brand-darkNavy border border-brand-lightBlue/40 px-4 py-3 text-white placeholder:text-dark-500 focus:outline-none focus:border-brand-yellow transition-colors uppercase"
        />
      </label>

      <label className="flex items-start gap-3 text-sm text-dark-300">
        <input
          type="checkbox"
          className="mt-1 accent-brand-yellow"
          checked={accepted}
          onChange={(e) => setAccepted(e.target.checked)}
        />
        <span>
          {t('becas.payment.terms')}{' '}
          <a href="/politicas?tab=terms" className="text-brand-yellow hover:underline" target="_blank" rel="noreferrer">
            {t('becas.payment.termsLink')}
          </a>
        </span>
      </label>

      {canUseStripe && stripePromise && (
        <Elements
          stripe={stripePromise}
          options={{
            mode: 'payment',
            amount: Math.max(amount, 50),
            currency: currency.toLowerCase(),
            appearance,
            locale: payment?.locale?.startsWith('es') ? 'es' : 'en',
          }}
        >
          <StripeCheckout
            accepted={accepted}
            paying={paying}
            payError={payError}
            discount={discount}
            onPay={onPay}
          />
        </Elements>
      )}

      {!canUseStripe && (
        <>
          {!publishableKey && (
            <p className="text-sm text-dark-400">{t('becas.payment.missingKey')}</p>
          )}
          {payError && (
            <p className="text-sm text-accent-rose bg-accent-rose/10 border border-accent-rose/30 rounded-xl px-4 py-3">
              {payError}
            </p>
          )}
          <Button
            type="button"
            variant="primary"
            fullWidth
            isLoading={paying}
            disabled={!accepted || !discount.trim()}
            onClick={() => void onPay(undefined, discount.trim() || undefined)}
          >
            {t('becas.payment.applyDiscount')}
          </Button>
        </>
      )}
    </div>
  );
}

function StripeCheckout({
  accepted,
  paying,
  payError,
  discount,
  onPay,
}: {
  accepted: boolean;
  paying: boolean;
  payError: string | null;
  discount: string;
  onPay: (paymentToken?: string, discountCode?: string) => Promise<void>;
}) {
  const { t } = useTranslation();
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handlePay = async (e: FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements || !accepted) return;
    setSubmitting(true);
    setError(null);
    const submitted = await elements.submit();
    if (submitted.error) {
      setError(submitted.error.message || t('becas.errors.payment'));
      setSubmitting(false);
      return;
    }
    const { error: methodError, paymentMethod } = await stripe.createPaymentMethod({ elements });
    if (methodError || !paymentMethod?.id) {
      setError(methodError?.message || t('becas.errors.payment'));
      setSubmitting(false);
      return;
    }
    try {
      await onPay(paymentMethod.id, discount.trim() || undefined);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('becas.errors.generic'));
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handlePay} className="space-y-4">
      <p className="text-sm text-dark-400">{t('becas.payment.secureHint')}</p>
      <PaymentElement />
      {(error || payError) && (
        <p className="text-sm text-accent-rose bg-accent-rose/10 border border-accent-rose/30 rounded-xl px-4 py-3">
          {error || payError}
        </p>
      )}
      <Button
        type="submit"
        variant="primary"
        fullWidth
        isLoading={submitting || paying}
        disabled={!stripe || !accepted}
      >
        {t('becas.payment.pay')}
      </Button>
    </form>
  );
}
